import { useState } from 'react';
import { useHasPermission } from '@/auth/auth-store';
import { useAllTerminalParams, type TerminalParamAssignment } from '@/hooks/use-terminal-params';
import { targetScopeId, type EditorTarget } from './editor-target';
import { ParameterEditor } from './parameter-editor';

/**
 * The in-context placements of the parameter editor (RL-279, TP-FR-009,
 * TP-FR-010): the terminal-profile page pinned to a profile, the terminal
 * detail page pinned to a terminal. No navigation entry of their own — like
 * the peripherals editor, they live where the thing they configure lives.
 *
 * One component in three placements is the requirement, so this file adds
 * only a disclosure around `ParameterEditor`: a one-line summary of what is
 * set at this scope, and the same editor underneath when opened.
 */

/** "3 parameters set on this profile" — what the disclosure reads when closed. */
export function pinnedSummary(
  rows: readonly TerminalParamAssignment[],
  target: EditorTarget,
): string {
  if (target.scope === 'global') return 'Global parameters';
  const scopeId = targetScopeId(target);
  const count = rows.filter((row) => row.scope === target.scope && row.scopeId === scopeId).length;
  const where = target.scope === 'profile' ? 'on this profile' : 'on this terminal';
  if (count === 0) return `No parameters set ${where}`;
  return `${count} parameter${count === 1 ? '' : 's'} set ${where}`;
}

export function PinnedParametersToggle({
  target,
  open,
  onToggle,
}: {
  target: EditorTarget;
  open: boolean;
  onToggle: () => void;
}) {
  const canRead = useHasPermission('terminal-params.read');
  const rows = useAllTerminalParams(canRead);
  if (!canRead) return null;
  const summary = rows.isError
    ? 'Parameters unavailable'
    : rows.data === undefined
      ? 'Parameters…'
      : pinnedSummary(rows.data, target);
  return (
    <button
      type="button"
      className="block text-left text-xs text-muted-foreground underline-offset-2 hover:underline"
      aria-expanded={open}
      aria-label={`${open ? 'Hide' : 'Show'} parameters for ${targetName(target)}`}
      onClick={onToggle}
    >
      {summary} — {open ? 'hide' : 'edit'}
    </button>
  );
}

function targetName(target: EditorTarget): string {
  switch (target.scope) {
    case 'global':
      return 'the estate';
    case 'profile':
      return target.profileName;
    case 'terminal':
      return target.terminalLabel;
  }
}

/** Toggle and editor together, for a placement with room for both. */
export function PinnedParameters({ target }: { target: EditorTarget }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      <PinnedParametersToggle target={target} open={open} onToggle={() => setOpen((o) => !o)} />
      {open && <ParameterEditor target={target} />}
    </div>
  );
}
